import React, { useContext, useRef } from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { useConsent } from "../../consent/ConsentContext";
import { CartContext } from "../../context/shopContext";
import Subscription from "./subscription";
import useIsVisible from "../../hooks/isVisible";
import CustomLink from "./navbar/Mobile/CustomLink";

const Footer = () => {
  const { openSettings } = useConsent();
  const { sanityFooter } = useStaticQuery(graphql`
    {
      sanityFooter {
        logo {
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              width: 150
              placeholder: BLURRED
              formats: WEBP
            )
          }
        }
        subfooterLinks {
          menutitle
          linkType
          slug {
            current
          }
          pdf {
            asset {
              url
            }
          }
          url
          id
        }
        infoLinks {
          menutitle
          linkType
          slug {
            current
          }
          pdf {
            asset {
              url
            }
          }
          url
          id
        }
        helpLinks {
          menutitle
          linkType
          slug {
            current
          }
          pdf {
            asset {
              url
            }
          }
          url
          id
        }
        locations {
          title
          slug {
            current
          }
        }
        newsletterFormTitle
        iosAppLink
        iosIcon {
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: WEBP
            )
          }
        }
        androidAppLink
        androidIcon {
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: WEBP
            )
          }
        }
        socialLinksTitle
        contactEmail
        socialLinks {
          title
          url
          iconImage {
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                width: 50
                height: 50
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
        }
      }
    }
  `);
  const elemRef = useRef();

  const isVisible = useIsVisible(elemRef);
  const locations = sanityFooter.locations;

  // const { myProfile } = useContext(CartContext);

  return (
    <footer className="bg-[#f5f5f5] flex flex-col-reverse md:flex-col shadow-top shadow-primary/50 mt-3">
      <div className="block md:hidden pb-[42px] px-3 sm:px-10">
        <p className="text-xs font-dinnext font-light mb-[2px]">Contact Us</p>
        <div className="flex flex-col space-y-4">
          <a
            className="font-dinnext font-medium"
            href={`mailto:${sanityFooter.contactEmail}`}
          >
            {sanityFooter.contactEmail}
            <span className="normal-case"> (opens email app)</span>
          </a>
          <div className="flex items-center space-x-[17px]">
            {sanityFooter.socialLinks.map((link, key) => (
              <a
                key={key}
                href={link.url}
                target={
                  link.url?.includes("order.simplysalad.com")
                    ? "_self"
                    : "_blank"
                }
                rel="noopener noreferrer"
                className="w-[25px] h-[25px]"
                aria-label={`${link.title} social profile (opens in new tab)`}
              >
                <GatsbyImage
                  image={link.iconImage.asset.gatsbyImageData}
                  alt={link.title}
                  className="w-[25px] h-[25px] object-contain"
                  objectFit="contain"
                />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full px-3 sm:px-10 flex items-start justify-between py-[42px] md:py-20 flex-col-reverse md:flex-row">
        <div className="xl:flex-[0.7]">
          <div className="gap-10 md:gap-[106px] grid grid-cols-2 xl:grid-cols-3 mt-[31px] md:mt-0">
            <div className="flex flex-col space-y-1">
              <p className="text-xl font-bold leading-[26px] capitalize">
                Information
              </p>
              {sanityFooter.infoLinks.map((link, index) => (
                <CustomLink
                  cls="text-base leading-[26px] capitalize"
                  data={link}
                  key={index}
                />
              ))}
            </div>
            <div className="flex flex-col space-y-1">
              <p className="text-xl font-bold leading-[26px] capitalize">
                Help
              </p>
              {sanityFooter.helpLinks.map((link, index) => (
                <CustomLink
                  cls="text-base leading-[26px] capitalize"
                  data={link}
                  key={index}
                />
              ))}
            </div>
            {locations && locations.length > 0 && (
              <div className="flex flex-col space-y-1">
                <span className="text-xl font-bold leading-[26px] capitalize">
                  Locations
                </span>
                {locations.map((location, index) => (
                  <Link
                    key={index}
                    to={`/locations/${location.slug.current}`}
                    className="text-base leading-[26px] capitalize"
                  >
                    {location.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="gap-10 sm:gap-[99px] md:gap-[106px] grid grid-cols-2 xl:grid-cols-3 mt-[31px]">
            <div className="mt-5">
              <p className="text-xl font-bold leading-[26px] capitalize">
                Download our app
              </p>
              <div className="flex flex-col gap-4 mt-3">
                <a
                  href={sanityFooter.iosAppLink}
                  target={
                    sanityFooter.iosAppLink?.includes("order.simplysalad.com")
                      ? "_self"
                      : "_blank"
                  }
                  rel="noopener noreferrer"
                  className="w-32 h-14"
                  aria-label="Download our iOS App, redirects to off-site App store"
                >
                  <GatsbyImage
                    image={sanityFooter.iosIcon.asset.gatsbyImageData}
                    alt="IOS App"
                    className="w-32 h-14 object-contain"
                    objectFit="contain"
                  />
                </a>
                <a
                  href={sanityFooter.androidAppLink}
                  target={
                    sanityFooter.androidAppLink?.includes(
                      "order.simplysalad.com"
                    )
                      ? "_self"
                      : "_blank"
                  }
                  rel="noopener noreferrer"
                  className="w-32 h-14"
                  aria-label="Download our Android App, redirects to off-site Googleplay store"
                >
                  <GatsbyImage
                    image={sanityFooter.androidIcon.asset.gatsbyImageData}
                    alt="Android App"
                    className="w-32 h-14 object-contain"
                    objectFit="contain"
                  />
                </a>
              </div>
            </div>
            <div className="mt-5">
              <p className="text-xl font-bold leading-[26px] capitalize">
                Connect with Us
              </p>
              <div className="flex items-center space-x-[17px] mt-3">
                {sanityFooter.socialLinks.map((link, key) => (
                  <a
                    key={key}
                    href={link.url}
                    target={
                      link.url?.includes("order.simplysalad.com")
                        ? "_self"
                        : "_blank"
                    }
                    rel="noopener noreferrer"
                    className="w-[25px] h-[25px]"
                    aria-label={`${link.title} social profile (opens in new tab)`}
                  >
                    <GatsbyImage
                      image={link.iconImage.asset.gatsbyImageData}
                      alt={link.title}
                      className="w-[25px] h-[25px] object-contain"
                      objectFit="contain"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="xl:flex-[0.3]">
          <div ref={elemRef} className="in-view">
            {isVisible && (
              <Subscription title={sanityFooter.newsletterFormTitle} />
            )}
          </div>
          <div className="hidden md:block mt-[55px]">
            <span className="text-xs font-dinnext font-light mb-[2px]">
              Contact Us
            </span>
            <div className="flex items-end space-x-4">
              <a
                href={`mailto:${sanityFooter.contactEmail}`}
              >
                {sanityFooter.contactEmail}
                <span className="normal-case"> (opens email app)</span>
              </a>
            </div>
          </div>
        </div>{" "}
      </div>
      {/* Subfooter */}
      <div className="w-full h-[91px] md:h-20 flex items-center justify-between pl-2 sm:pl-6 pr-3 sm:pr-11 border-x-0 border-b-0 border-t border-solid border-t-[#C4C4C4]">
        <div>
          <GatsbyImage
            image={sanityFooter.logo.asset.gatsbyImageData}
            alt="Simply Salad"
            className="w-[160px] h-12 object-contain"
            objectFit="contain"
          />
          {/* <p className="text-[9.5px] ml-[14px] font-dinnext font-light">
            {sanityFooter.logoCaption}
          </p> */}
        </div>
        <div className="md:space-x-10 w-40 md:w-auto flex flex-col md:flex-row items-start md:items-center">
          {sanityFooter.subfooterLinks.map((link, index) => (
            <CustomLink
              cls="text-sm sm:text-base leading-[26px] capitalize font-dinnext font-light"
              data={link}
              key={index}
            />
          ))}
          <button
            type="button"
            onClick={openSettings}
            className="text-sm sm:text-base leading-[26px] capitalize font-dinnext font-light min-h-[44px] text-left"
          >
            Cookie settings
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
